<template>
  <div class="search-hotels min-h-screen">
    <!-- Location Permission Modal -->
    <div
      v-if="showLocationModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        class="fixed inset-0 bg-ink-900/50"
        @click="dismissLocationModal"
      ></div>
      <div
        class="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-warm-lg"
      >
        <div class="mb-4 flex items-start justify-between">
          <h5 class="font-display text-lg font-semibold text-ink-900">
            <i class="bi bi-geo-alt-fill mr-2 text-primary-600"></i>
            Enable Location Services
          </h5>
          <button
            type="button"
            class="text-ink-400 transition-colors hover:text-ink-700"
            @click="dismissLocationModal"
            aria-label="Close"
          >
            <svg
              class="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div>
          <p class="mb-3 text-sm text-ink-700">
            To provide you with the best experience and show nearby hotels, we
            need access to your location.
          </p>
          <div
            class="rounded-lg border border-sand-200 bg-cream-100 p-4 text-sm text-ink-700"
          >
            <i class="bi bi-info-circle mr-2 text-primary-600"></i>
            <strong>Benefits:</strong>
            <ul class="mb-0 mt-2 list-disc space-y-1 pl-5">
              <li>Find hotels near you automatically</li>
              <li>Get accurate distance information</li>
              <li>Personalized search results</li>
            </ul>
          </div>
        </div>
        <div class="mt-5 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-sand-300 px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-cream-100"
            @click="dismissLocationModal"
          >
            Not Now
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-warm-sm transition-colors hover:bg-primary-600 disabled:opacity-60"
            @click="enableLocation"
            :disabled="loadingLocation"
          >
            <span v-if="loadingLocation" class="inline-flex items-center">
              <span class="spinner spinner-sm mr-2"></span>
              Getting Location...
            </span>
            <span v-else class="inline-flex items-center">
              <i class="bi bi-geo-alt-fill mr-2"></i>
              Enable Location
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- SEO Meta Tags -->
    <div style="display: none">
      <h1>Hotels and Apartments Search - Roomsvile</h1>
      <p>
        Find and book hotels, serviced apartments, and vacation rentals in
        {{ selectedCityName || "your area" }}. Best prices guaranteed. Search by
        location, state, or city.
      </p>
    </div>

    <!-- Hero Section with Gradient Background -->
    <section
      class="relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-gold-600 text-white"
    >
      <!-- Animated Background Pattern -->
      <div class="pointer-events-none absolute inset-0 opacity-10">
        <div
          class="absolute -left-48 top-0 h-96 w-96 rotate-45 rounded-full bg-white"
        ></div>
        <div
          class="absolute bottom-0 right-0 h-96 w-96 -rotate-45 rounded-full bg-white"
        ></div>
      </div>

      <div class="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20">
        <div class="py-4 text-center">
          <h1
            class="mb-3 font-display text-4xl font-semibold text-white sm:text-5xl"
          >
            Discover Your Perfect Stay
          </h1>
          <p class="mb-3 text-lg text-white/90">
            Book directly with hotels and apartments. No middleman, no extra
            fees.
          </p>
          <div
            class="mb-6 flex items-center justify-center gap-2 font-semibold text-gold-200"
          >
            <i class="bi bi-patch-check-fill text-lg"></i>
            <span>Direct Booking • Best Prices • No Hidden Fees</span>
          </div>

          <!-- Quick Search Bar -->
          <div class="flex justify-center">
            <div
              class="w-full max-w-3xl rounded-2xl bg-white p-3 shadow-warm-lg"
            >
              <div class="grid grid-cols-1 gap-2 md:grid-cols-12">
                <div class="md:col-span-7">
                  <input
                    v-model="quickSearchQuery"
                    type="text"
                    placeholder="Enter city, state, or property name..."
                    class="w-full rounded-lg border border-sand-300 bg-white px-4 py-2.5 text-sm text-ink-800 placeholder:text-ink-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    @keyup.enter="performQuickSearch"
                  />
                </div>
                <div class="md:col-span-2">
                  <button
                    @click="toggleAdvancedSearch"
                    class="w-full rounded-lg border border-sand-300 px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-cream-100"
                  >
                    Filters
                  </button>
                </div>
                <div class="md:col-span-3">
                  <button
                    @click="performQuickSearch"
                    class="inline-flex w-full items-center justify-center rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-warm-sm transition-colors hover:bg-primary-600"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Advanced Search Modal/Panel -->
    <transition name="slide-fade">
      <section
        v-if="showAdvancedSearch"
        class="border-b-4 border-primary-500 bg-cream-100 py-14 md:py-20"
      >
        <div class="mx-auto max-w-6xl px-4 sm:px-6">
          <div
            class="rounded-2xl border border-sand-200 bg-white shadow-warm-lg"
          >
            <div class="p-6 md:p-8">
              <div class="mb-6 flex items-center justify-between">
                <h2 class="font-display text-2xl font-semibold text-ink-900">
                  Advanced Search
                </h2>
                <button
                  @click="toggleAdvancedSearch"
                  class="text-ink-400 transition-colors hover:text-ink-700"
                >
                  <svg
                    class="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div class="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <!-- Location-based Search -->
                <div class="col-span-1">
                  <div
                    class="h-full rounded-2xl border border-primary-200 bg-primary-50 p-6"
                  >
                    <h3
                      class="mb-4 flex items-center font-display text-lg font-semibold text-ink-900"
                    >
                      <svg
                        class="mr-2 h-6 w-6 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Search Nearby
                    </h3>
                    <button
                      @click="searchByLocation"
                      :disabled="loadingLocation"
                      class="inline-flex w-full items-center justify-center rounded-lg bg-primary-500 py-3 text-sm font-semibold text-white shadow-warm-sm transition-colors hover:bg-primary-600 disabled:opacity-60"
                    >
                      {{
                        loadingLocation
                          ? "Getting Location..."
                          : "Use My Location"
                      }}
                    </button>
                    <p
                      v-if="currentLocation"
                      class="mt-3 rounded-lg bg-white p-3 text-sm text-ink-800"
                    >
                      📍 {{ currentLocation }}
                    </p>
                    <p
                      v-if="locationError"
                      class="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-600"
                    >
                      {{ locationError }}
                    </p>
                  </div>
                </div>

                <!-- State/City Search -->
                <div class="col-span-1">
                  <div
                    class="h-full rounded-2xl border border-gold-200 bg-gold-50 p-6"
                  >
                    <h3
                      class="mb-4 flex items-center font-display text-lg font-semibold text-ink-900"
                    >
                      <svg
                        class="mr-2 h-6 w-6 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      Search by Location
                    </h3>
                    <div>
                      <div class="mb-3">
                        <label
                          class="mb-1.5 block text-sm font-medium text-ink-700"
                          >Select State</label
                        >
                        <select
                          v-model="selectedState"
                          @change="onStateChange"
                          class="w-full rounded-lg border border-sand-300 bg-white px-4 py-2.5 text-sm text-ink-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                          :disabled="locationStore.isLoadingStates"
                        >
                          <option value="">Choose a state...</option>
                          <option
                            v-for="state in locationStore.states"
                            :key="state.id"
                            :value="state.id"
                          >
                            {{ state.name }}
                          </option>
                        </select>
                      </div>
                      <div v-if="selectedState" class="mb-3">
                        <label
                          class="mb-1.5 block text-sm font-medium text-ink-700"
                          >Select City</label
                        >
                        <select
                          v-model="selectedCity"
                          class="w-full rounded-lg border border-sand-300 bg-white px-4 py-2.5 text-sm text-ink-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                          :disabled="locationStore.isLoadingCities"
                        >
                          <option value="">Choose a city...</option>
                          <option
                            v-for="city in locationStore.cities"
                            :key="city.id"
                            :value="city.id"
                          >
                            {{ city.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Additional Filters -->
              <div class="mb-6">
                <label class="mb-1.5 block text-sm font-medium text-ink-700"
                  >Property Type</label
                >
                <select
                  v-model="propertyType"
                  class="w-full rounded-lg border border-sand-300 bg-white px-4 py-2.5 text-sm text-ink-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">All Types</option>
                  <option value="1">Hotels</option>
                  <option value="2">Apartments</option>
                </select>
              </div>

              <button
                @click="searchHotels"
                class="inline-flex w-full items-center justify-center rounded-lg bg-primary-500 py-3 text-base font-semibold text-white shadow-warm-sm transition-colors hover:bg-primary-600"
              >
                Apply Filters &amp; Search
              </button>
            </div>
          </div>
        </div>
      </section>
    </transition>

    <!-- Featured Hotels & Apartments Section -->
    <section v-if="!hasSearched" class="bg-white py-14 md:py-20">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="mb-6">
          <h2 class="mb-1 font-display text-2xl font-semibold text-ink-900">
            Featured Properties
          </h2>
          <p class="text-ink-500">
            Hotels, apartments, and vacation rentals listed by property owners
          </p>
        </div>

        <!-- Loading State -->
        <div v-if="loadingFeaturedHotels" class="py-14 text-center">
          <div class="spinner" role="status" aria-label="Loading"></div>
          <p class="mt-3 text-ink-500">Loading featured hotels...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="featuredHotels.length === 0" class="py-14 text-center">
          <i class="bi bi-building mb-3 text-5xl text-ink-300"></i>
          <p class="text-ink-500">
            No featured hotels available at the moment.
          </p>
        </div>

        <!-- Featured Hotels Horizontal Scroll -->
        <div v-else class="featured-hotels-scroll">
          <div class="featured-hotels-container">
            <div
              v-for="hotel in featuredHotels"
              :key="'featured-' + hotel.id"
              class="featured-hotel-card"
            >
              <PropertyCard
                :hotel="hotel"
                :is-favourite="favouritesStore.isFavourite(hotel.id)"
                :favourite-pending="favouritesStore.isPending(hotel.id)"
                @toggle-favourite="toggleFavourite"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Nearby Hotels Loading State -->
    <section
      v-if="!hasSearched && isLoadingNearby && nearbyHotels.length === 0"
      class="bg-cream-100 py-14 md:py-20"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="py-14 text-center">
          <div class="spinner" role="status" aria-label="Loading"></div>
          <p class="mt-3 text-ink-500">Finding properties near you...</p>
        </div>
      </div>
    </section>

    <!-- Nearby Hotels Section -->
    <section
      v-if="!hasSearched && nearbyHotels.length > 0"
      class="bg-cream-100 py-14 md:py-20"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="mb-6 flex items-center justify-between">
          <div>
            <h2 class="mb-1 font-display text-2xl font-semibold text-ink-900">
              Properties Near You
            </h2>
            <p class="mb-0 text-ink-500">
              Based on your current location
              <span v-if="nearbyPagination" class="ml-2">
                (Showing {{ nearbyHotels.length }} of
                {{ nearbyPagination.totalRecords }})
              </span>
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <PropertyCard
            v-for="hotel in nearbyHotels"
            :key="'nearby-' + hotel.id"
            :hotel="hotel"
            :is-favourite="favouritesStore.isFavourite(hotel.id)"
            :favourite-pending="favouritesStore.isPending(hotel.id)"
            @toggle-favourite="toggleFavourite"
          />
        </div>

        <!-- Load More Button -->
        <div v-if="hasMoreNearbyPages" class="mt-6 text-center">
          <button
            @click="loadMoreNearbyHotels"
            :disabled="isLoadingNearby"
            class="rounded-lg border border-sand-300 px-8 py-3 text-base font-semibold text-ink-700 hover:bg-cream-100 disabled:opacity-60"
          >
            <span v-if="isLoadingNearby" class="inline-flex items-center">
              <span class="spinner spinner-sm mr-2"></span>
              Loading...
            </span>
            <span v-else class="inline-flex items-center">
              Load More Hotels
              <i class="bi bi-arrow-down-circle ml-2"></i>
            </span>
          </button>
        </div>
      </div>
    </section>

    <!-- Results Section -->
    <section
      v-if="hasSearched && isLoadingNearby"
      class="bg-white py-14 md:py-20"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="py-14 text-center">
          <div class="spinner" role="status" aria-label="Loading"></div>
          <p class="mt-3 text-ink-500">Searching for properties...</p>
        </div>
      </div>
    </section>

    <section
      v-else-if="hasSearched && nearbyHotels.length > 0"
      class="bg-white py-14 md:py-20"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="mb-6 flex items-center justify-between">
          <div>
            <h2 class="mb-1 font-display text-2xl font-semibold text-ink-900">
              {{ nearbyPagination?.totalRecords || nearbyHotels.length }}
              Properties Found
            </h2>
            <p class="mb-0 text-ink-500">
              {{
                selectedCityName
                  ? `in ${selectedCityName}, ${selectedStateName}`
                  : "near you"
              }}
              • Direct booking available
            </p>
          </div>
          <button
            @click="clearSearch"
            class="rounded-lg border border-sand-300 px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-cream-100"
          >
            <i class="bi bi-x-circle mr-1"></i> Clear Search
          </button>
        </div>

        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <PropertyCard
            v-for="hotel in nearbyHotels"
            :key="'result-' + hotel.id"
            :hotel="hotel"
            :is-favourite="favouritesStore.isFavourite(hotel.id)"
            :favourite-pending="favouritesStore.isPending(hotel.id)"
            @toggle-favourite="toggleFavourite"
          />
        </div>

        <!-- Load More Button -->
        <div v-if="hasMoreNearbyPages" class="mt-6 text-center">
          <button
            @click="loadMoreNearbyHotels"
            :disabled="isLoadingNearby"
            class="rounded-lg border border-sand-300 px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-cream-100 disabled:opacity-60"
          >
            <span v-if="isLoadingNearby" class="inline-flex items-center">
              <span class="spinner spinner-sm mr-2"></span>
              Loading...
            </span>
            <span v-else> Load More Properties </span>
          </button>
        </div>
      </div>
    </section>

    <!-- No Results -->
    <section
      v-else-if="hasSearched && nearbyHotels.length === 0 && !isLoadingNearby"
      class="bg-white py-14 md:py-20"
    >
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="text-center">
          <i class="bi bi-search mb-3 text-5xl text-ink-300"></i>
          <h2 class="mb-3 font-display text-2xl font-semibold text-ink-900">
            No Properties Found
          </h2>
          <p class="mb-6 text-ink-500">
            We couldn't find any properties matching your criteria. Try
            adjusting your filters.
          </p>
          <button
            @click="clearSearch"
            class="inline-flex items-center justify-center rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-warm-sm transition-colors hover:bg-primary-600"
          >
            Clear Search
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useSEO } from "@/composables/useSEO";
import { useHotelStore } from "@/stores/hotel.store";
import { useLocationStore } from "@/stores/location.store";
import { useAuthStore } from "@/stores/auth.store";
import { useFavouritesStore } from "@/stores/favourites.store";
import PropertyCard from "@/components/shared/PropertyCard.vue";

export default defineComponent({
  name: "SearchHotelsView",
  components: { PropertyCard },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const hotelStore = useHotelStore();
    const locationStore = useLocationStore();
    const authStore = useAuthStore();
    const favouritesStore = useFavouritesStore();

    const toggleFavourite = async (hotelId: string) => {
      if (!authStore.isAuthenticated) {
        router.push({ path: "/login", query: { redirect: route.fullPath } });
        return;
      }
      try {
        await favouritesStore.toggleFavourite(hotelId);
      } catch {
        // Error is available via favouritesStore.error if surfaced later.
      }
    };

    // Get reactive references from hotel store
    const {
      featuredHotels,
      nearbyHotels,
      isLoading: loadingFeaturedHotels,
      isLoadingNearby,
      nearbyPagination,
    } = storeToRefs(hotelStore);

    // Computed from store
    const hasMoreNearbyPages = computed(() => hotelStore.hasMoreNearbyPages);

    // State management
    const loadingLocation = ref(false);
    const currentLocation = ref("");
    const locationError = ref("");
    const selectedState = ref("");
    const selectedCity = ref("");
    const propertyType = ref("");
    const hasSearched = ref(false);
    const showAdvancedSearch = ref(false);
    const quickSearchQuery = ref("");
    const showLocationModal = ref(false);
    const locationPermissionDismissed = ref(false);

    // Computed properties
    const selectedCityName = computed(() => {
      return locationStore.selectedCity?.name || "";
    });

    const selectedStateName = computed(() => {
      return locationStore.selectedState?.name || "";
    });

    const canSearch = computed(() => {
      return (
        currentLocation.value !== "" ||
        (selectedState.value !== "" && selectedCity.value !== "")
      );
    });

    // Methods
    const onStateChange = () => {
      selectedCity.value = "";
      if (selectedState.value) {
        locationStore.setSelectedState(selectedState.value);
      }
    };

    const toggleAdvancedSearch = () => {
      showAdvancedSearch.value = !showAdvancedSearch.value;
    };

    const performQuickSearch = async () => {
      if (!quickSearchQuery.value.trim()) {
        showAdvancedSearch.value = true;
        return;
      }
      hasSearched.value = true;
      showAdvancedSearch.value = false;

      // Call API with search term only
      await hotelStore.fetchNearbyHotels(
        undefined,
        undefined,
        quickSearchQuery.value.trim(),
        undefined,
        undefined,
        undefined
      );

      // Scroll to results
      setTimeout(() => {
        window.scrollTo({ top: 600, behavior: "smooth" });
      }, 100);
    };

    const dismissLocationModal = () => {
      showLocationModal.value = false;
      locationPermissionDismissed.value = true;
      localStorage.setItem("locationPermissionDismissed", "true");
    };

    const enableLocation = async () => {
      loadingLocation.value = true;

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            // Mark as not dismissed so modal won't show again
            localStorage.removeItem("locationPermissionDismissed");

            // Close modal
            showLocationModal.value = false;

            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            currentLocation.value = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

            // Fetch nearby hotels from store
            await hotelStore.fetchNearbyHotels(lat, lng);

            loadingLocation.value = false;
          },
          (error) => {
            showLocationModal.value = false;
            loadingLocation.value = false;
            locationError.value =
              "Unable to retrieve your location. Please enable location services in your browser settings.";
            console.error("Geolocation error:", error);

            // Show an alert to help user
            alert(
              "Please enable location services in your browser settings to see nearby hotels."
            );
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      } else {
        showLocationModal.value = false;
        loadingLocation.value = false;
        locationError.value = "Geolocation is not supported by your browser.";
      }
    };

    const searchByLocation = async () => {
      loadingLocation.value = true;
      locationError.value = "";

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            currentLocation.value = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

            // Reset all other filters
            selectedState.value = "";
            selectedCity.value = "";
            quickSearchQuery.value = "";
            propertyType.value = "";
            locationStore.clearSelection();
            hasSearched.value = true;
            showAdvancedSearch.value = false;

            // Fetch nearby hotels from store with only location
            await hotelStore.fetchNearbyHotels(
              lat,
              lng,
              undefined,
              undefined,
              undefined,
              undefined
            );

            loadingLocation.value = false;

            // Scroll to results
            setTimeout(() => {
              window.scrollTo({ top: 600, behavior: "smooth" });
            }, 100);
          },
          (error) => {
            locationError.value =
              "Unable to retrieve your location. Please enable location services.";
            loadingLocation.value = false;
            console.error("Geolocation error:", error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      } else {
        locationError.value = "Geolocation is not supported by your browser.";
        loadingLocation.value = false;
      }
    };

    const loadMoreNearbyHotels = async () => {
      await hotelStore.loadMoreNearbyHotels();
    };

    const searchHotels = async () => {
      hasSearched.value = true;
      showAdvancedSearch.value = false;

      // Build search parameters from selected filters
      await hotelStore.fetchNearbyHotels(
        undefined,
        undefined,
        quickSearchQuery.value.trim() || undefined,
        selectedState.value || undefined,
        selectedCity.value || undefined,
        propertyType.value || undefined
      );

      // Scroll to results
      setTimeout(() => {
        window.scrollTo({ top: 600, behavior: "smooth" });
      }, 100);
    };

    const clearSearch = async () => {
      // Store location data before clearing
      const locationData = hotelStore.userLocation;

      hasSearched.value = false;
      quickSearchQuery.value = "";
      selectedState.value = "";
      selectedCity.value = "";
      propertyType.value = "";
      locationStore.clearSelection();
      currentLocation.value = "";

      // If location data exists, fetch nearby hotels with location only (silently in background)
      if (locationData && locationData.latitude && locationData.longitude) {
        await hotelStore.fetchNearbyHotels(
          locationData.latitude,
          locationData.longitude,
          undefined,
          undefined,
          undefined,
          undefined
        );
      } else {
        // No location data, clear everything
        hotelStore.nearbyHotels = [];
        hotelStore.nearbyPagination = null;
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      // SEO Setup
      const siteUrl = process.env.VUE_APP_SITE_URL || "https://roomsvile.com";

      useSEO(
        {
          title: "Roomsvile - Book Hotels, Short Lets & Apartments in Nigeria",
          description:
            "Book hotels, short let and serviced apartments across Nigeria with Roomsvile. Find places to stay in Lagos, Abuja, Port Harcourt and every state at the best prices.",
          image: `${siteUrl}/images/default-og.jpg`,
          url: siteUrl,
          type: "website",
          keywords:
            "hotel search, find hotels, search hotels, hotel finder, apartment search, find apartments, accommodation finder, room finder, hotel deals, hotel offers, hotel prices, hotel rates, compare hotels, hotel comparison, filter hotels by price, filter hotels by amenities, hotels by location, hotels by state, nearby hotels, featured hotels, top rated hotels, popular hotels, recommended hotels, available rooms, room availability, check in check out, hotel search engine, vacation rentals, short let search, apartment finder nigeria",
        },
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Roomsvile",
          description:
            "Book hotels, short lets and serviced apartments in Nigeria",
          url: siteUrl,
          potentialAction: {
            "@type": "SearchAction",
            target: `${siteUrl}/?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }
      );

      // Fetch featured hotels from store
      hotelStore.fetchFeaturedHotels();

      // Fetch states from location store
      locationStore.fetchStates();

      // Check location permission status
      const checkLocationPermission = async () => {
        if ("permissions" in navigator && "geolocation" in navigator) {
          try {
            const permissionStatus = await navigator.permissions.query({
              name: "geolocation" as PermissionName,
            });

            if (permissionStatus.state === "granted") {
              // Location already granted, fetch nearby hotels silently
              navigator.geolocation.getCurrentPosition(
                async (position) => {
                  await hotelStore.fetchNearbyHotels(
                    position.coords.latitude,
                    position.coords.longitude,
                    undefined,
                    undefined,
                    undefined,
                    undefined
                  );
                },
                () => {
                  // Silently fail
                },
                {
                  timeout: 15000,
                  maximumAge: 300000,
                }
              );
            } else {
              // Permission is either prompt or denied, show modal if not dismissed
              const dismissed = localStorage.getItem(
                "locationPermissionDismissed"
              );
              if (!dismissed) {
                setTimeout(() => {
                  showLocationModal.value = true;
                }, 2000);
              }
            }
          } catch (error) {
            // Fallback for browsers that don't support permissions API
            const dismissed = localStorage.getItem(
              "locationPermissionDismissed"
            );
            if (!dismissed) {
              setTimeout(() => {
                showLocationModal.value = true;
              }, 2000);
            }
          }
        } else if ("geolocation" in navigator) {
          // Fallback for older browsers without permissions API
          const dismissed = localStorage.getItem("locationPermissionDismissed");
          if (!dismissed) {
            setTimeout(() => {
              showLocationModal.value = true;
            }, 2000);
          }
        }
      };

      checkLocationPermission();
    });

    return {
      // Stores
      locationStore,
      favouritesStore,

      // State
      loadingLocation,
      currentLocation,
      locationError,
      selectedState,
      selectedCity,
      propertyType,
      hasSearched,
      showAdvancedSearch,
      quickSearchQuery,
      featuredHotels,
      loadingFeaturedHotels,
      nearbyHotels,
      isLoadingNearby,
      nearbyPagination,
      showLocationModal,

      // Computed
      selectedCityName,
      selectedStateName,
      canSearch,
      hasMoreNearbyPages,

      // Methods
      onStateChange,
      toggleAdvancedSearch,
      performQuickSearch,
      searchByLocation,
      searchHotels,
      clearSearch,
      dismissLocationModal,
      enableLocation,
      loadMoreNearbyHotels,
      toggleFavourite,
    };
  },
});
</script>

<style scoped>
/* Smooth Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f3e9d8;
}

::-webkit-scrollbar-thumb {
  background: #c3ac8d;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #2563eb;
}

/* Range Input Styling */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

input[type="range"]::-webkit-slider-track {
  background: linear-gradient(to right, #3b82f6, #c9932b);
  height: 6px;
  border-radius: 3px;
}

input[type="range"]::-moz-range-track {
  background: linear-gradient(to right, #3b82f6, #c9932b);
  height: 6px;
  border-radius: 3px;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  border: 3px solid #3b82f6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(59, 32, 20, 0.2);
}

input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  border: 3px solid #3b82f6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(59, 32, 20, 0.2);
}

/* Animation for background pattern */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

/* Date input styling */
input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer;
  filter: brightness(0) saturate(100%) invert(30%) sepia(45%) saturate(1200%)
    hue-rotate(340deg) brightness(90%) contrast(90%);
}

/* Focus states */
input:focus,
select:focus,
button:focus {
  outline: none;
}

/* Loading animation */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Featured Hotels Horizontal Scroll */
.featured-hotels-scroll {
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #2563eb #f3e9d8;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: 0.5rem;
}

.featured-hotels-scroll::-webkit-scrollbar {
  height: 8px;
}

.featured-hotels-scroll::-webkit-scrollbar-track {
  background: #f3e9d8;
  border-radius: 10px;
}

.featured-hotels-scroll::-webkit-scrollbar-thumb {
  background: #2563eb;
  border-radius: 10px;
}

.featured-hotels-scroll::-webkit-scrollbar-thumb:hover {
  background: #1d4ed8;
}

.featured-hotels-container {
  display: flex;
  gap: 1.5rem;
  /* Room for the hover lift, shadow and focus ring, which overflow clips */
  padding: 0.5rem 0.5rem 1.5rem;
}

.featured-hotel-card {
  flex: 0 0 auto;
  width: 340px;
  scroll-snap-align: start;
}

@media (max-width: 768px) {
  .featured-hotel-card {
    width: 300px;
  }
}

@media (max-width: 576px) {
  .featured-hotel-card {
    width: 82vw;
    max-width: 300px;
  }
}
</style>
